import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { InvitationInfoDto } from "@theadam/shared";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/form";
import { Alert, Spinner } from "@/components/ui/misc";
import { ApiError, api } from "@/lib/api";
import { AuthLayout } from "./AuthLayout";

/** Davet bağlantısı: yeni kişi ad ve şifre belirler; başka çalışma alanında hesabı olan mevcut şifresiyle katılır. */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const info = useQuery({ queryKey: ["invitation", token], queryFn: () => api.get<InvitationInfoDto>(`/auth/invitations/${encodeURIComponent(token)}`), enabled: Boolean(token), retry: false });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; fields?: Record<string, string> } | null>(null);
  const i = info.data;
  const mismatch = !i?.existingAccount && confirm.length > 0 && confirm !== password;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/auth/invitations/${encodeURIComponent(token)}/accept`, i?.existingAccount ? { password } : { name: name.trim(), password });
      navigate("/giris", { replace: true, state: { invited: true } });
    } catch (err) {
      setError(err instanceof ApiError ? { message: err.message, fields: err.fields } : { message: "Davet kabul edilemedi" });
      setBusy(false);
    }
  };

  if (!token || info.error)
    return (
      <AuthLayout title="Davet geçersiz">
        <Alert tone="warning" title={info.error instanceof ApiError ? info.error.message : "Davet bağlantısı eksik veya hatalı."}>
          <Link className="underline" to="/giris">
            Giriş sayfasına dön
          </Link>
        </Alert>
      </AuthLayout>
    );
  if (!i) return <Spinner label="Davet kontrol ediliyor" />;

  return (
    <AuthLayout title={`${i.tenantName} paneline katılın`} subtitle={`${i.email} · ${i.roleName}`}>
      <form onSubmit={(e) => void submit(e)} noValidate className="space-y-5">
        {error && <Alert tone="danger" title={error.message} />}
        {i.existingAccount ? (
          <>
            <Alert tone="info" title="Bu e-postayla zaten bir hesabınız var">
              Katılmak için bu hesapta kullandığınız şifreyi girin; sonra aynı şifreyle giriş yaparsınız.
            </Alert>
            <Field label="Mevcut şifreniz" error={error?.fields?.password}>
              {(p) => <Input {...p} type="password" autoComplete="current-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />}
            </Field>
          </>
        ) : (
          <>
            <Field label="Ad soyad" error={error?.fields?.name}>
              {(p) => <Input {...p} autoComplete="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />}
            </Field>
            <Field label="Şifre" hint="En az 12 karakter; büyük harf, küçük harf ve rakam içermeli." error={error?.fields?.password}>
              {(p) => <Input {...p} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
            </Field>
            <Field label="Şifre (tekrar)" error={mismatch ? "Şifreler aynı değil" : undefined}>
              {(p) => <Input {...p} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
            </Field>
          </>
        )}
        <Button type="submit" className="w-full" loading={busy} disabled={!password || mismatch || (!i.existingAccount && (name.trim().length < 2 || !confirm))}>
          Daveti kabul et
        </Button>
      </form>
    </AuthLayout>
  );
}
